import type { InputBinding } from '@/types/multiplayer';
import { captureError } from '@/sentry';

export type BackgroundId = 'graph' | 'scifi' | 'skyline';

export interface BackgroundConfig {
  id: BackgroundId;
  label: string;
  file: string;
  darkness: number;
}

export const BACKGROUNDS: BackgroundConfig[] = [
  { id: 'graph', label: 'Graph', file: '/assets/images/bg-graph.png', darkness: 0.6 },
  { id: 'scifi', label: 'Sci-Fi', file: '/assets/images/bg-scifi.jpg', darkness: 0.6 },
  { id: 'skyline', label: 'Skyline', file: '/assets/images/bg-skyline.jpg', darkness: 0.4 },
];

export interface GameSettings {
  inputKey: string;
  inputBindings: InputBinding[];
  sfxVolume: number;
  musicVolume: number;
  background: BackgroundId;
}

const SETTINGS_KEY = 'morse-hero-settings';

const defaultSettings: GameSettings = {
  inputKey: 'SPACE',
  inputBindings: [{ playerId: 0, type: 'keyboard', key: 'SPACE' }],
  sfxVolume: 0.7,
  musicVolume: 0.5,
  background: 'graph',
};

export function getBackgroundConfig(id: BackgroundId): BackgroundConfig {
  return BACKGROUNDS.find(bg => bg.id === id) ?? BACKGROUNDS[0];
}

export function loadSettings(): GameSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const inputBindings = Array.isArray(parsed.inputBindings) && parsed.inputBindings.length > 0
        ? parsed.inputBindings
        : defaultSettings.inputBindings;
      return { ...defaultSettings, ...parsed, inputBindings };
    }
  } catch (error) {
    captureError(error, { scope: 'settings', action: 'loadSettings' });
  }
  return { ...defaultSettings };
}

export function saveSettings(settings: Partial<GameSettings>): GameSettings {
  const current = loadSettings();
  const updatedBindings = settings.inputBindings ?? current.inputBindings ?? defaultSettings.inputBindings;
  const updated = { ...current, ...settings, inputBindings: updatedBindings };

  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
  } catch (error) {
    captureError(error, { scope: 'settings', action: 'saveSettings' });
  }

  return updated;
}

export function resetSettings(): GameSettings {
  try {
    localStorage.removeItem(SETTINGS_KEY);
  } catch (error) {
    captureError(error, { scope: 'settings', action: 'resetSettings' });
  }
  return { ...defaultSettings };
}
